import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TextAreaWithAiAssistant } from '@client/components/_shared/ai/TextAreaWithAiAssistant';
import { UploadControlV2 } from '@client/components/_shared/documents/UploadControlV2';
import { CostCodeSelect } from '@client/components/_shared/selectors/financial/CostCodeSelect';
import { TransactionTypeSelect } from '@client/components/_shared/selectors/financial/TransactionTypeSelect';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { buildCurrencyInputRules } from '@client/utils';
import { capture } from '@client/utils/analytics';
import { createFileRoute } from '@tanstack/react-router';
import { Divider, Form, Select, Tag } from 'antd';
import type { Decimal } from 'decimal.js';
import { useEffect, useId } from 'react';
import { claimFormStore } from '../../store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/reserves/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Add reserve',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ReserveAdd />
        </PageController>
    );
}

type Reserve = {
    transactionTypeId: bigint;
    costCodeId: bigint;
    description: string;
    amount: Decimal;
    isAutoReserveAmountApplied: boolean;
    payee: string;
};

const FormItem = Form.Item<Reserve>;

function ReserveAdd() {
    const formId = useId();
    const isReadonly = claimFormStore.use.isReadonly();
    const claim = claimFormStore.use.formData();
    const { trpcUtils } = useTrpc();
    const { onFileListChange, files } = UploadControlV2.useUploadControlV2();
    const { mutate } = trpc.reserve.createReserve.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<Reserve>();
    const isAutoReserveAmountApplied = Form.useWatch(
        ({ isAutoReserveAmountApplied }) => isAutoReserveAmountApplied,
        form,
    );

    useEffect(() => {
        if (isReadonly) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly]);

    if (isReadonly) {
        return null;
    }

    return (
        <OverlayPage
            title="Add reserve"
            afterClose={() => {
                form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" form={formId}>
                        Save
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    form={form}
                    id={formId}
                    layout="vertical"
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(
                            {
                                description: values.description,
                                claimId: claim.id,
                                transactionTypeId: values.transactionTypeId,
                                costCodeId: values.costCodeId,
                                amount: values.amount,
                                isAutoReserveAmountApplied: values.isAutoReserveAmountApplied,
                                files: files.map((item) => ({
                                    key: item.key,
                                    name: item.name,
                                    description: item.description,
                                    categories: item.categories,
                                    mimeType: item.mimeType,
                                    size: item.size,
                                    additionalInfo: item.additionalInfo,
                                })),
                            },
                            {
                                onSuccess() {
                                    message.success('Saved.');
                                    capture('reserve_created', { module: 'claim' });
                                    void trpcUtils.reserve.invalidate();
                                    void trpcUtils.claim.getFinancialSummary.invalidate();
                                    onClose();
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    {claim.productTypeId && (
                        <>
                            <FormItem label="Transaction type" name="transactionTypeId" rules={[{ required: true }]}>
                                <TransactionTypeSelect
                                    transactionNature="RESERVE"
                                    productTypeId={claim.productTypeId}
                                />
                            </FormItem>
                            <FormItem label="Cost code" name="costCodeId" rules={[{ required: true }]}>
                                <CostCodeSelect
                                    productTypeId={claim.productTypeId}
                                    onChange={(_, { autoReserveAmount }) => {
                                        form.setFieldsValue({
                                            isAutoReserveAmountApplied: !!autoReserveAmount,
                                            amount: autoReserveAmount || undefined,
                                        });
                                    }}
                                />
                            </FormItem>
                        </>
                    )}
                    <FormItem
                        label={
                            <>
                                Reserve amount
                                {isAutoReserveAmountApplied && (
                                    <Tag variant="outlined" className="ml-1 text-xs" color="warning">
                                        Auto reserve
                                    </Tag>
                                )}
                            </>
                        }
                        name="amount"
                        rules={buildCurrencyInputRules({ isRequired: true, gtZero: true })}
                    >
                        <CurrencyInput
                            onChange={() =>
                                form.setFieldsValue({
                                    isAutoReserveAmountApplied: false,
                                })
                            }
                        />
                    </FormItem>
                    <FormItem name="isAutoReserveAmountApplied" hidden />
                    <FormItem label="Payee" name="payee" rules={[{ required: true }]}>
                        <Select
                            options={[{ value: 'Policy holder' }, { value: 'Supplier' }, { value: 'Third party' }]}
                        />
                    </FormItem>
                    <FormItem label="Description" name="description" rules={[{ required: true }]}>
                        <TextAreaWithAiAssistant relatedFieldLabel="Claim - Description" rows={4} />
                    </FormItem>

                    <Divider titlePlacement="start">Attachments</Divider>

                    <UploadControlV2
                        categories={['Reserve']}
                        purpose="entityAttachment"
                        onFileListChange={onFileListChange}
                    />
                </Form>
            )}
        </OverlayPage>
    );
}
